/**
 * DirectionIcon (#2254 → #2348) — the branded wallet +/− transaction glyph
 * (wallet-＋ = income, wallet-− = expense), used by both liquidity feeds.
 *
 * #2254 shipped this as four downscaled PNG illustrations (24/48px 1x/2x,
 * ``sips``-reduced from 1254×1254 RGB sources with no alpha channel). An
 * illustration can't survive downscaling into an ~18px box, so it read as an
 * illegible smudge; the no-alpha white corners even needed a border-radius
 * clip hack against the tinted EntityTile plate.
 *
 * #2348 replaces them with an icon-grade, purpose-drawn inline <svg> glyph:
 * a wallet body + snap button + a bold ±, a strong silhouette that stays
 * crisp at 16–20px. It draws in ``currentColor``, so it inherits the plate's
 * direction tint automatically (EntityTile's ``--success`` green / ``--warning``
 * orange). Because it's an <svg>, it re-enters EntityTile's icon sizing rule
 * (``.entity-tile__icon svg { width/height: 1.125rem }``) — no self-sized box,
 * no CSS clip hack, no committed raster assets.
 *
 * The accessible name from the retired ``<img alt>`` is preserved via
 * ``role="img"`` + ``aria-label`` + a ``<title>``.
 */

export default function DirectionIcon({ income }: { income: boolean }) {
  const label = income ? 'Einnahme' : 'Ausgabe';
  return (
    <svg
      className={`liquiditaetsplanung-direction-icon liquiditaetsplanung-direction-icon--${income ? 'income' : 'expense'}`}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={label}
    >
      <title>{label}</title>
      {/* Wallet body — soft-filled, outlined pocket. */}
      <rect
        x="3.25"
        y="6.5"
        width="16.25"
        height="11"
        rx="2.6"
        fill="currentColor"
        fillOpacity="0.14"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      {/* Snap button on the wallet's right edge — the wallet cue. */}
      <circle cx="16.4" cy="12" r="1.2" fill="currentColor" />
      {/* Direction mark: ＋ for income, − for expense (bold, centred left). */}
      {income ? (
        <path d="M9.5 9.6v4.8M7.1 12h4.8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      ) : (
        <path d="M7.1 12h4.8" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      )}
    </svg>
  );
}
